import type { FC } from 'react';

import { NodeLabelFetcher } from '../../breadcrumbs/NodeLabelFetcher';
import type { GetBreadcrumbLabelByNodeType } from '../../breadcrumbs/types';
import { useBreadcrumbs } from '../../breadcrumbs/useBreadcrumbs';
import { useNodeLabels } from '../../breadcrumbs/useNodeLabels';
import { BreadcrumbDisplay } from './BreadcrumbDisplay';
import { CopyLinkButton } from './CopyLinkButton';

interface Props {
  getBreadcrumbLabelByNodeType?: GetBreadcrumbLabelByNodeType;
}

export const GlobalBreadcrumbs: FC<Props> = ({
  getBreadcrumbLabelByNodeType,
}) => {
  const { breadcrumbs, nodes } = useBreadcrumbs();
  const { allLoaded } = useNodeLabels(nodes);
  const loading = !allLoaded;

  return (
    <div data-testid={'global-breadcrumbs'} className={'w-full min-w-0'}>
      <NodeLabelFetcher
        nodes={nodes}
        getBreadcrumbLabelByNodeType={getBreadcrumbLabelByNodeType}
      />

      <div className={'flex gap-3 items-center'}>
        <div
          className={`transition-opacity duration-500 ${loading ? 'opacity-25' : 'opacity-100'}`}
        >
          <CopyLinkButton disabled={loading} />
        </div>
        <div className={'w-full min-w-0'}>
          {/*
            RSP-5956: only mount the Cloudscape BreadcrumbGroup once every dynamic crumb label
            has resolved. The group collapses its whole trail into a "▼ <leaf>" dropdown when its
            ResizeObserver (useContainerQuery) measures a transiently-narrow width — which is what
            happens if it mounts during the cold-load window while the header flex layout is still
            settling (the case for Compliance/Indicators, whose record title isn't yet in the
            Apollo cache; Risks/Controls have it warm, so they never hit this). Deferring the
            mount until labels are loaded guarantees the group's first and only measurement runs
            against a settled, full-width container, so the full inline trail renders.
          */}
          {loading ? (
            <div
              data-testid={'breadcrumb-skeleton'}
              aria-hidden={true}
              className={
                'h-5 w-40 max-w-full animate-pulse rounded bg-white/10'
              }
            />
          ) : (
            <BreadcrumbDisplay breadcrumbs={breadcrumbs} />
          )}
        </div>
      </div>
    </div>
  );
};
